import { Popover } from '@headlessui/react';
import { Check, RotateCcw } from 'lucide-react';
import { createPortal } from 'react-dom';
import { useRef, useEffect, useState } from 'react';
import { PREDEFINED_COLORS, DEFAULT_WARRANTY_COLOR } from '../constants/colors';

interface ColorPickerPopoverProps {
  value: string | null | undefined;
  onChange: (color: string | null) => void;
  disabled?: boolean;
  size?: 'sm' | 'md';
}

export const ColorPickerPopover = ({ value, onChange, disabled = false, size = 'md' }: ColorPickerPopoverProps) => {
  const currentColor = value || DEFAULT_WARRANTY_COLOR;
  const hasCustomColor = value && value !== DEFAULT_WARRANTY_COLOR;
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [position, setPosition] = useState({ top: 0, left: 0 });

  const sizeClasses = size === 'sm' ? 'tw-w-5 tw-h-5' : 'tw-w-8 tw-h-8';

  return (
    <Popover className="tw-relative">
      {({ open, close }) => {
        useEffect(() => {
          if (open && buttonRef.current) {
            const rect = buttonRef.current.getBoundingClientRect();
            const popoverWidth = 256;
            const viewportWidth = window.innerWidth;

            let left = rect.left + window.scrollX;

            if (left + popoverWidth > viewportWidth) {
              left = rect.right + window.scrollX - popoverWidth;
            }

            if (left < 0) {
              left = 8;
            }

            setPosition({
              top: rect.bottom + window.scrollY + 8,
              left,
            });
          }
        }, [open]);

        return (
          <>
            <Popover.Button
              ref={buttonRef}
              disabled={disabled}
              className={`${sizeClasses} tw-rounded-full tw-border-2 tw-border-gray-300 hover:tw-border-gray-400 tw-transition-all tw-shadow-sm hover:tw-shadow-md disabled:tw-opacity-50 disabled:tw-cursor-not-allowed`}
              style={{ backgroundColor: currentColor }}
              title="Changer la couleur"
            >
            </Popover.Button>

            {open && createPortal(
              <Popover.Panel
                static
                className="tw-fixed tw-bg-white tw-rounded-lg tw-shadow-xl tw-border tw-border-gray-200 tw-p-3 tw-w-64"
                style={{
                  top: `${position.top}px`,
                  left: `${position.left}px`,
                  zIndex: 9999
                }}
              >
            <div className="tw-mb-3">
              <p className="tw-text-xs tw-font-semibold tw-text-gray-700 tw-mb-2">
                Choisir une couleur
              </p>
              <div className="tw-grid tw-grid-cols-5 tw-gap-2">
                {PREDEFINED_COLORS.map((color) => {
                  const isSelected = currentColor === color.hex;
                  return (
                    <button
                      key={color.hex}
                      type="button"
                      onClick={() => {
                        onChange(color.hex === DEFAULT_WARRANTY_COLOR ? null : color.hex);
                        close();
                      }}
                      className={`tw-w-10 tw-h-10 tw-rounded-lg tw-border-2 tw-transition-all tw-relative tw-flex tw-items-center tw-justify-center ${
                        isSelected
                          ? 'tw-border-gray-900 tw-shadow-md tw-scale-110'
                          : 'tw-border-gray-300 hover:tw-border-gray-400 hover:tw-scale-105'
                      }`}
                      style={{ backgroundColor: color.hex }}
                      title={color.label}
                    >
                      {isSelected && (
                        <Check className="tw-w-5 tw-h-5 tw-text-white tw-drop-shadow" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {hasCustomColor && (
              <div className="tw-pt-3 tw-border-t tw-border-gray-200">
                <button
                  type="button"
                  onClick={() => {
                    onChange(null);
                    close();
                  }}
                  className="tw-w-full tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-gray-700 tw-bg-gray-100 hover:tw-bg-gray-200 tw-rounded-md tw-transition-colors tw-flex tw-items-center tw-justify-center tw-gap-2"
                >
                  <RotateCcw className="tw-w-3 tw-h-3" />
                  Réinitialiser la couleur
                </button>
              </div>
            )}
              </Popover.Panel>,
              document.body
            )}
          </>
        );
      }}
    </Popover>
  );
};
